import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import React, { forwardRef, useMemo, useState } from 'react';

interface YearFieldProps {
  topLabel?: string;
  value?: string | number;
  onChange?: (event: SelectChangeEvent<string | number>) => void;
  required?: boolean;
  helperText?: React.ReactNode;
  variant?: 'standard' | 'outlined' | 'filled';
  minYear?: number;
  maxYear?: number;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  [key: string]: any;
}

const YearField = forwardRef<HTMLDivElement, YearFieldProps>(function YearField(
  props: YearFieldProps,
  ref
) {
  const {
    topLabel,
    value,
    onChange,
    required,
    helperText,
    variant = 'outlined',
    minYear,
    maxYear,
    placeholder = 'Select Year',
    disabled = false,
    fullWidth = true,
    ...rest
  } = props;

  const [open, setOpen] = useState(false);

  // Generate years array dynamically
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = minYear || 1900;
    const endYear = maxYear || currentYear;

    const yearList: number[] = [];
    for (let year = endYear; year >= startYear; year--) {
      yearList.push(year);
    }
    return yearList;
  }, [minYear, maxYear]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    if (onChange) {
      onChange(event);
    }
  };

  // Determine if helper text should be displayed
  // Show helper text if it exists and is not the string "required" (case-insensitive)
  const isHelperTextString = typeof helperText === 'string';
  const isRequiredText = isHelperTextString && helperText.toLowerCase() === 'required';
  const shouldShowHelperText = helperText && !isRequiredText;

  return (
    <Box ref={ref}>
      {topLabel && (
        <Typography variant="body1" sx={{ fontWeight: '500', mb: '1rem' }}>
          {topLabel}
          {required && '*'}
        </Typography>
      )}
      <FormControl
        fullWidth={fullWidth}
        variant={variant}
        required={required}
        disabled={disabled}
        error={typeof helperText === 'string' && helperText?.toLowerCase() === 'required'}>
        <Select
          labelId="year-select-label"
          id="year-select"
          value={value || ''}
          onChange={handleChange}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          open={open}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return <em style={{ color: '#999' }}>{placeholder}</em>;
            }
            return selected.toString();
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300
              }
            }
          }}
          {...rest}>
          {years.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
        {shouldShowHelperText && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
            {helperText}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
});

export default YearField;
